import React, { useState, useEffect } from 'react';
import { ArrowRight, BarChart2, Shield, Users, Video, FileText, PlayCircle, CheckCircle2, TrendingUp, FolderPlus, MessageSquare, Bell, File as FileEdit, Table, MessageCircle, Ligature as FileSignature, Send, Video as VideoIcon, AlertCircle, X, Info, Mic, Lock, Globe, Search, UserCheck, Bot, Clock, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import VideoPlayer from './VideoPlayer';

const atsSlides = [
  '/Screenshot_2026-04-21_at_11.39.09_PM.png',
  '/Screenshot_2026-04-21_at_11.40.52_PM.png',
  '/Screenshot_2026-04-22_at_1.40.32_AM.png',
];

function ATSImageSlider() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % atsSlides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full rounded-xl overflow-hidden gradient-border glow-effect" style={{ paddingBottom: '75%' }}>
      {atsSlides.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`PerplexityATS screenshot ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-contain object-top transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 z-10">
        {atsSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-[#40E0D0] scale-125' : 'bg-white/40 hover:bg-white/70'}`}
          />
        ))}
      </div>
    </div>
  );
}

const products = [
  {
    name: 'PerplexityALM',
    description: 'Experience the future of streamlined test planning and execution with AI. Our Application Life-Cycle Management Software revolutionizes your testing workflow.',
    features: [
      { icon: <FileText className="w-6 h-6 text-primary" />, text: 'Create Testplans' },
      { icon: <PlayCircle className="w-6 h-6 text-primary" />, text: 'Create Test Runs' },
      { icon: <CheckCircle2 className="w-6 h-6 text-primary" />, text: 'Quality Assurance' },
      { icon: <TrendingUp className="w-6 h-6 text-primary" />, text: 'Quality Enhancement' },
      { icon: <CheckCircle2 className="w-6 h-6 text-primary" />, text: 'Approve Production Tickets' },
      { icon: <Shield className="w-6 h-6 text-primary" />, text: 'Risk Mitigation Approvals' }
    ],
    image: (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden gradient-border">
        <div className="absolute inset-0 bg-[#1a1f2a] p-4">
          {/* Dashboard Header */}
          <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-2">
            <div className="text-white text-sm font-semibold">PerplexityALM Dashboard</div>
            <button className="flex items-center gap-2 bg-[#40E0D0] bg-opacity-20 text-[#40E0D0] px-3 py-1 rounded-lg text-sm hover:bg-opacity-30 transition-all">
              <MessageCircle className="w-4 h-4" />
              Ask AI Assistant
            </button>
          </div>
          
          {/* Main Grid */}
          <div className="grid grid-cols-2 gap-4 h-[calc(100%-3rem)]">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Test Metrics */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#151922] p-3 rounded-lg">
                  <div className="text-gray-400 text-xs">Total Test Cases</div>
                  <div className="text-[#40E0D0] text-lg font-bold">2,847</div>
                  <div className="text-green-400 text-xs">↑ 156 this sprint</div>
                </div>
                <div className="bg-[#151922] p-3 rounded-lg">
                  <div className="text-gray-400 text-xs">Pass Rate</div>
                  <div className="text-[#40E0D0] text-lg font-bold">92.3%</div>
                  <div className="text-green-400 text-xs">↑ 3.2% vs last run</div>
                </div>
                <div className="bg-[#151922] p-3 rounded-lg">
                  <div className="text-gray-400 text-xs">Test Coverage</div>
                  <div className="text-[#40E0D0] text-lg font-bold">87.5%</div>
                  <div className="text-green-400 text-xs">↑ 5.1% this sprint</div>
                </div>
              </div>

              {/* Test Execution Trends */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-gray-300 text-sm">Test Execution Trends</div>
                  <div className="text-xs text-gray-400">Last 7 Days</div>
                </div>
                <div className="relative h-40">
                  <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gray-700"></div>
                  <div className="absolute inset-y-0 left-0 w-[1px] bg-gray-700"></div>
                  <svg className="w-full h-full" viewBox="0 0 300 150" preserveAspectRatio="none">
                    <path
                      d="M0 100 L50 90 L100 85 L150 70 L200 60 L250 40 L300 30"
                      fill="none"
                      stroke="#40E0D0"
                      strokeWidth="2"
                    />
                    <path
                      d="M0 100 L50 90 L100 85 L150 70 L200 60 L250 40 L300 30"
                      fill="url(#gradient)"
                      fillOpacity="0.1"
                    />
                  </svg>
                </div>
              </div>

              {/* Test Status Distribution */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-gray-300 text-sm">Test Status Distribution</div>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
                      <span className="text-xs text-gray-400">Passed (92%)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#EF4444]"></div>
                      <span className="text-xs text-gray-400">Failed (5%)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#F59E0B]"></div>
                      <span className="text-xs text-gray-400">Blocked (3%)</span>
                    </div>
                  </div>
                </div>
                <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden">
                  <div className="absolute h-full bg-[#40E0D0]" style={{ width: '92%' }}></div>
                  <div className="absolute h-full bg-[#EF4444] left-[92%]" style={{ width: '5%' }}></div>
                  <div className="absolute h-full bg-[#F59E0B] left-[97%]" style={{ width: '3%' }}></div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Recent Test Runs */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="text-gray-300 text-sm mb-4">Recent Test Runs</div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 bg-[#1E2330] rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
                      <span className="text-sm text-gray-300">Sprint 24 Regression</span>
                    </div>
                    <span className="text-xs text-gray-400">98% passed</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-[#1E2330] rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
                      <span className="text-sm text-gray-300">API Integration Tests</span>
                    </div>
                    <span className="text-xs text-gray-400">95% passed</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-[#1E2330] rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#EF4444]"></div>
                      <span className="text-sm text-gray-300">Payment Gateway Tests</span>
                    </div>
                    <span className="text-xs text-gray-400">82% passed</span>
                  </div>
                </div>
              </div>

              {/* AI Insights */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="text-gray-300 text-sm mb-2">AI Test Insights</div>
                <div className="space-y-3">
                  <div className="bg-[#1E2330] p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-[#40E0D0] text-sm mb-1">
                      <TrendingUp className="w-4 h-4" />
                      Test Coverage Analysis
                    </div>
                    <p className="text-gray-400 text-xs">AI suggests adding more API integration tests for the payment module to improve coverage.</p>
                  </div>
                  <div className="bg-[#1E2330] p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-[#40E0D0] text-sm mb-1">
                      <AlertCircle className="w-4 h-4" />
                      Risk Assessment
                    </div>
                    <p className="text-gray-400 text-xs">High-risk areas identified in authentication flow - recommended increasing security test cases.</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="text-gray-300 text-sm mb-3">Quick Actions</div>
                <div className="grid grid-cols-2 gap-2">
                  <button className="flex items-center gap-2 bg-[#1E2330] p-2 rounded-lg text-sm text-gray-300 hover:bg-[#252a3a] transition-colors">
                    <PlayCircle className="w-4 h-4 text-[#40E0D0]" />
                    New Test Run
                  </button>
                  <button className="flex items-center gap-2 bg-[#1E2330] p-2 rounded-lg text-sm text-gray-300 hover:bg-[#252a3a] transition-colors">
                    <FileText className="w-4 h-4 text-[#40E0D0]" />
                    Create Test Plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    link: 'https://PerplexityALM.com'
  },
  {
    name: 'Workday Development Services',
    isATS: true,
    description: 'Scale Your Hiring Capacity',
    features: [
      { icon: <Search className="w-6 h-6 text-primary" />, text: 'Built exclusively for Workday hiring' },
      { icon: <UserCheck className="w-6 h-6 text-primary" />, text: 'Access pre-vetted Workday SMEs instantly' },
      { icon: <Clock className="w-6 h-6 text-primary" />, text: 'Reduce time-to-fill and vendor overhead' },
      { icon: <LayoutDashboard className="w-6 h-6 text-primary" />, text: 'Manage contract + direct hire in one system' },
      { icon: <Users className="w-6 h-6 text-primary" />, text: 'No per-seat pricing—built for teams' },
      { icon: <Bot className="w-6 h-6 text-primary" />, text: 'Post unlimited Workday roles' },
    ],
    image: <ATSImageSlider />,
    link: 'https://Perplexity180.com'
  },
  {
    name: 'Community Connect',
    description: 'For Workday Professionals and Consultants',
    features: [
      { icon: <MessageSquare className="w-6 h-6 text-primary" />, text: 'Post Workday Insights' },
      { icon: <Mic className="w-6 h-6 text-primary" />, text: 'Start a Podcast' },
      { icon: <Lock className="w-6 h-6 text-primary" />, text: 'Community-Only Posts' },
    ],
    additionalDescription: 'Share your Workday insights, hard to solve configuration discoveries, ask questions, connect with fellow consultants, start a podcast, or simply share about your Work-Day. This is Perplexity\'s Workday community platform.',
    image: (
      <div className="relative w-full rounded-xl overflow-hidden gradient-border bg-[#0a0f1a]" style={{ paddingBottom: '52%' }}>
        <img
          src="/COmmunity_connect.png"
          alt="Community Connect platform screenshot"
          className="absolute inset-0 w-full h-full object-contain object-center"
        />
        <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/90 via-black/60 to-transparent flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-1.5">
              <div className="w-6 h-6 rounded-full bg-[#40E0D0] border-2 border-black flex items-center justify-center font-bold text-black" style={{fontSize:'8px'}}>WD</div>
              <div className="w-6 h-6 rounded-full bg-blue-400 border-2 border-black flex items-center justify-center font-bold text-black" style={{fontSize:'8px'}}>JR</div>
              <div className="w-6 h-6 rounded-full bg-amber-400 border-2 border-black flex items-center justify-center font-bold text-black" style={{fontSize:'8px'}}>SM</div>
              <div className="w-6 h-6 rounded-full bg-rose-400 border-2 border-black flex items-center justify-center font-bold text-black" style={{fontSize:'8px'}}>KL</div>
              <div className="w-6 h-6 rounded-full bg-emerald-400 border-2 border-black flex items-center justify-center font-bold text-black" style={{fontSize:'7px'}}>+106K</div>
            </div>
            <span className="text-white font-medium" style={{fontSize:'11px'}}>Workday Professionals</span>
          </div>
          <div className="flex gap-1">
            <div className="bg-white/10 backdrop-blur-sm border border-[#40E0D0]/30 rounded px-1.5 py-0.5 flex items-center gap-0.5">
              <Globe className="w-2.5 h-2.5 text-[#40E0D0]" />
              <span className="text-gray-200" style={{fontSize:'9px'}}>Public Post</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-[#40E0D0]/50 rounded px-1.5 py-0.5 flex items-center gap-0.5">
              <Lock className="w-2.5 h-2.5 text-[#40E0D0]" />
              <span className="text-[#40E0D0] font-medium" style={{fontSize:'9px'}}>Community Only</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-[#40E0D0]/40 rounded px-1.5 py-0.5 flex items-center gap-0.5">
              <MessageSquare className="w-2.5 h-2.5 text-[#40E0D0]" />
              <span className="text-gray-200" style={{fontSize:'9px'}}>DM &amp; Group Chat</span>
            </div>
          </div>
        </div>
      </div>
    ),
    link: '/community',
    isCommunity: true,
  },
  {
    name: 'Perplexity Meet',
    description: 'Our platform empowers small business, government agencies and veteran-owned organizations to enhance collaboration through our conferencing app.',
    features: [
      { icon: <Shield className="w-6 h-6 text-primary" />, text: 'Secure Meeting Rooms' },
      { icon: <Users className="w-6 h-6 text-primary" />, text: 'Robust Content-Sharing' },
      { icon: <Video className="w-6 h-6 text-primary" />, text: 'AI-Powered Interactive Labs' }
    ],
    image: (
      <VideoPlayer 
        videoUrl="https://vonarzenmlzoyzawdnui.supabase.co/storage/v1/object/public/media/perplexitymeet-mp4/Perplexity%20Meet_Comp%202_1.mp4" 
        autoplay={false} 
      />
    ),
    link: 'https://perplexitymeet.com'
  },
  {
    name: 'Reports Detective',
    description: 'Transform Data into Actionable Intelligence with AI-Powered Analytics and Insights.',
    features: [
      { icon: <BarChart2 className="w-6 h-6 text-primary" />, text: 'Advanced AI Data Analytics' },
      { icon: <Shield className="w-6 h-6 text-primary" />, text: 'Secure Data Processing' },
      { icon: <Users className="w-6 h-6 text-primary" />, text: 'BI Insights & More' }
    ],
    image: (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden gradient-border">
        <div className="absolute inset-0 bg-[#1a1f2a] p-4">
          {/* Dashboard Header */}
          <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-2">
            <div className="text-white text-sm font-semibold">Data Analytics Dashboard</div>
            <button className="flex items-center gap-2 bg-[#40E0D0] bg-opacity-20 text-[#40E0D0] px-3 py-1 rounded-lg text-sm hover:bg-opacity-30 transition-all">
              <MessageCircle className="w-4 h-4" />
              Ask AI Assistant
            </button>
          </div>
          
          {/* Main Grid */}
          <div className="grid grid-cols-2 gap-4 h-[calc(100%-3rem)]">
            {/* Left Column */}
            <div className="space-y-4">
              {/* KPI Cards */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#151922] p-3 rounded-lg">
                  <div className="text-gray-400 text-xs">Total Employees</div>
                  <div className="text-[#40E0D0] text-lg font-bold">156k</div>
                  <div className="text-green-400 text-xs">↑ 12% vs last month</div>
                </div>
                <div className="bg-[#151922] p-3 rounded-lg">
                  <div className="text-gray-400 text-xs">Retention Rate</div>
                  <div className="text-[#40E0D0] text-lg font-bold">94.8%</div>
                  <div className="text-green-400 text-xs">↑ 2.1% vs last month</div>
                </div>
                <div className="bg-[#151922] p-3 rounded-lg">
                  <div className="text-gray-400 text-xs">Performance Score</div>
                  <div className="text-[#40E0D0] text-lg font-bold">4.2/5</div>
                  <div className="text-green-400 text-xs">↑ 0.3 vs last quarter</div>
                </div>
              </div>

              {/* Performance Trends */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-gray-300 text-sm">Performance Trends</div>
                  <div className="text-xs text-gray-400">Last 6 Months</div>
                </div>
                <div className="relative h-40">
                  <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gray-700"></div>
                  <div className="absolute inset-y-0 left-0 w-[1px] bg-gray-700"></div>
                  <svg className="w-full h-full" viewBox="0 0 300 150" preserveAspectRatio="none">
                    <path
                      d="M0 120 L50 100 L100 110 L150 80 L200 90 L250 70 L300 60"
                      fill="none"
                      stroke="#40E0D0"
                      strokeWidth="2"
                    />
                    <path
                      d="M0 120 L50 100 L100 110 L150 80 L200 90 L250 70 L300 60"
                      fill="url(#gradient)"
                      fillOpacity="0.1"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#40E0D0" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#40E0D0" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>

              {/* Department Distribution */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-gray-300 text-sm">Department Distribution</div>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
                      <span className="text-xs text-gray-400">Tech (45%)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#9333EA]"></div>
                      <span className="text-xs text-gray-400">Sales (35%)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#60A5FA]"></div>
                      <span className="text-xs text-gray-400">Other (20%)</span>
                    </div>
                  </div>
                </div>
                <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden">
                  <div className="absolute h-full bg-[#40E0D0]" style={{ width: '45%' }}></div>
                  <div className="absolute h-full bg-[#9333EA] left-[45%]" style={{ width: '35%' }}></div>
                  <div className="absolute h-full bg-[#60A5FA] left-[80%]" style={{ width: '20%' }}></div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Key Performance Indicators */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="text-gray-300 text-sm mb-4">Key Performance Indicators</div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Employee Engagement</span>
                      <span className="text-[#40E0D0]">87%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full">
                      <div className="h-full bg-[#40E0D0] rounded-full" style={{ width: '87%' }}></div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Training Completion</span>
                      <span className="text-[#40E0D0]">92%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full">
                      <div className="h-full bg-[#40E0D0] rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Goal Achievement</span>
                      <span className="text-[#40E0D0]">78%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full">
                      <div className="h-full bg-[#40E0D0] rounded-full" style={{ width: '78%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Insights */}
              <div className="bg-[#151922] p-4 rounded-lg">
                <div className="text-gray-300 text-sm mb-2">AI Insights</div>
                <div className="space-y-3">
                  <div className="bg-[#1E2330] p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-[#40E0D0] text-sm mb-1">
                      <TrendingUp className="w-4 h-4" />
                      Performance Trends
                    </div>
                    <p className="text-gray-400 text-xs">Overall performance has increased by 15% in Q4 2023, with the Tech department showing the highest improvement.</p>
                  </div>
                  <div className="bg-[#1E2330] p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-[#40E0D0] text-sm mb-1">
                      <Users className="w-4 h-4" />
                      Workforce Insights
                    </div>
                    <p className="text-gray-400 text-xs">Employee satisfaction in remote teams has improved by 23% after implementing new collaboration tools.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    link: 'https://reportsdetective.com'
  }
];

function ReleaseModal({ onClose, message }: { onClose: () => void; message: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative z-10 bg-[#151922] border border-[#40E0D0]/30 rounded-2xl p-8 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center justify-center mb-5">
          <div className="p-3 rounded-full bg-emerald-500/10">
            <CheckCircle2 className="w-7 h-7 text-emerald-400" />
          </div>
        </div>
        <p className="text-gray-300 leading-relaxed text-center">
          {message}
        </p>
        <button
          onClick={onClose}
          className="mt-6 w-full gradient-border bg-[#40E0D0]/10 text-white px-6 py-3 rounded-lg font-medium hover:bg-[#40E0D0]/20 transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function ProductSection() {
  const [releaseModalMessage, setReleaseModalMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  return (
    <>
      {releaseModalMessage && <ReleaseModal onClose={() => setReleaseModalMessage(null)} message={releaseModalMessage} />}
      <section id="products" className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-16 text-center heading-gradient">
            Our Products
          </h2>

          <div className="space-y-32">
            {products.map((product, index) => (
              <div key={product.name} className={`flex flex-col lg:flex-row gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}>
                <div className="w-full lg:w-1/2">
                  <div className="glow-effect">
                    {product.image || (
                      <div className="relative w-full aspect-video rounded-xl overflow-hidden gradient-border bg-card-bg flex items-center justify-center">
                        <span className="text-gray-400">Image not available</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-full lg:w-1/2 space-y-8">
                  {(product as any).isATS ? (
                    <div className="card-gradient rounded-xl p-8 glow-effect space-y-6">
                      {/* Headline */}
                      <div>
                        <h3 className="text-2xl sm:text-3xl font-bold mb-4 gradient-text">PerplexityATS</h3>
                        <p className="text-xl font-semibold text-white mt-2 mb-4">Scale Your Workday Hiring Capacity</p>
                        <p className="text-gray-300 mt-1 leading-relaxed">
                          Stop paying for seats, slots, and disconnected tools.<br />
                          Move your Workday hiring into one platform.
                        </p>
                      </div>

                      {/* Free badge */}
                      <div>
                        <p className="text-[#40E0D0] font-semibold text-sm tracking-wide uppercase">Workday Customers &amp; Jobseekers</p>
                      </div>

                      {/* Subline */}
                      <p className="text-gray-300 leading-relaxed">
                        Post unlimited Workday roles, connect with pre-screened SMEs, and streamline contracting, recruiting, onboarding, and assignment management—all in one platform.
                      </p>

                      {/* Big heading */}
                      <div className="border-t border-[#40E0D0]/20 pt-5">
                        <p className="text-lg sm:text-xl font-bold text-white leading-snug mb-2">
                          Perplexity Workday Development Services
                        </p>
                        <p className="text-gray-300 leading-relaxed">
                          Always free for Workday customers and jobseekers. Built-into PerplexityATS — Perplexity's contracting business processes that simplify contracting and direct-hire placement via Perplexity Workday Development Services.
                        </p>
                      </div>

                      {/* Why section */}
                      <div>
                        <p className="text-[#40E0D0] font-semibold text-sm mb-3 tracking-wide uppercase">Why PerplexityATS?</p>
                        <ul className="space-y-2">
                          {product.features.map((feature) => (
                            <li key={feature.text} className="flex items-center gap-3">
                              <div className="flex-shrink-0 p-1.5 rounded-full bg-[rgba(64,224,208,0.1)]">
                                {feature.icon}
                              </div>
                              <span className="text-gray-300 text-sm">{feature.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Closing tagline */}
                      <div className="border-t border-[#40E0D0]/20 pt-4">
                        <p className="text-gray-300 leading-relaxed">
                          Stop paying for seats, slots, and disconnected tools.<br />
                          Move your Workday hiring into one platform.
                        </p>
                      </div>

                      {/* CTA */}
                      <a
                        href="/workday-services"
                        className="gradient-border bg-[#151922] text-white px-6 py-3 rounded-lg inline-flex items-center hover:bg-[#1a1f2a] transition-colors glow-effect"
                      >
                        Learn More
                        <ArrowRight className="ml-2" />
                      </a>
                    </div>
                  ) : (
                  <div className="card-gradient rounded-xl p-8 glow-effect">
                    <h3 className="text-2xl sm:text-3xl font-bold mb-4 gradient-text">{product.name}</h3>
                    <p className="text-gray-300 mb-8">{product.description}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                      {product.features.map((feature) => (
                        <div key={feature.text} className="flex flex-col items-center text-center space-y-2 p-3 card-gradient rounded-lg">
                          <div className="p-2 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect">
                            {feature.icon}
                          </div>
                          <span className="text-sm text-gray-300">{feature.text}</span>
                        </div>
                      ))}
                    </div>

                    {product.name === 'PerplexityALM' && (
                      <p className="text-gray-300 mb-8">
                        Revolutionize change management, moving beyond traditional top-down methods to embrace team empowerment in go live agile processes, and data-informed personalization to navigate rapid, complex Workday development optimizations, enhancements & transformations.
                      </p>
                    )}

                    {(product as any).additionalDescription && (
                      <p className="text-gray-300 mb-8">{(product as any).additionalDescription}</p>
                    )}

                    {(product as any).isCommunity ? (
                      <button
                        type="button"
                        onClick={() => navigate('/community')}
                        className="gradient-border bg-[#151922] text-white px-6 py-3 rounded-lg inline-flex items-center hover:bg-[#1a1f2a] transition-colors glow-effect cursor-pointer"
                      >
                        Learn More
                        <ArrowRight className="ml-2" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setReleaseModalMessage(
                          product.name === 'PerplexityALM'
                            ? 'Undergoing updates and enhancements, will be available once completed.'
                            : 'Undergoing updates and enhancements will be available again once complete.'
                        )}
                        className="gradient-border bg-[#151922] text-white px-6 py-3 rounded-lg inline-flex items-center hover:bg-[#1a1f2a] transition-colors glow-effect cursor-pointer"
                      >
                        Learn More
                        <ArrowRight className="ml-2" />
                      </button>
                    )}
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default ProductSection;